import {useCallback, useMemo, useRef, useState} from 'react';
// @ts-expect-error
import {useForm} from 'react-hook-form';
import {useDispatch} from 'react-redux';
import type {RouteComponentProps} from 'react-router-dom';

import {useMutation} from '@apollo/react-hooks';
import {DndContext} from '@dnd-kit/core';
import type {DragEndEvent, DragStartEvent} from '@dnd-kit/core';
import _ from 'underscore';

import {
    safelyGetGraphQLErrorMessage,
    type GraphQLErrorObject,
} from 'nutshell-core/utils/graphql-errors';

import {ConfirmationDialog} from 'shells/confirmation-dialog/confirmation-dialog';
import {FullPageEditor} from 'shells/layout/full-page-editor';
import {useDidMountEffect} from 'shells/utils/hooks/use-did-mount-effect';

import type {
    CreateField as CreateFieldMutation,
    CreateFieldVariables as CreateFieldMutationVariables,
    MultiPageFormFragment,
    FormPublishInput,
    FormUpdateInput,
} from 'nutshell-graphql-types';

import {useCreateCustomField} from '../../../core/custom-field/graphql/hooks/use-create-custom-field';
import type {FormSchemaProperty} from '../../../core/nutshell-fields/types';

import {fetchSchemaAction} from '../../forms-actions';
import CREATE_FIELD from '../../graphql/mutations/create-field.graphql';
import {convertGraphQLResponseToFormFieldState} from '../../helpers';

import {PublishModal} from '../publish-modal/publish-modal';

import {FormEditorDndLayout} from './drag-and-drop/form-editor-dnd-layout';
import {
    formEditorListCollisionDetection,
    handleFormEditorDragEnd,
    handleFormEditorDragStart,
} from './drag-and-drop/helpers';
import type {PageFieldArrayOps} from './drag-and-drop/types';
import {FormEditorMainContent} from './editor/form-editor-main-content';
import {convertMultiPageFormToGraphQL} from './helpers';
import {
    buildFieldCreateInputForExistingCustomSchemaField,
    formFieldRowToSelectedExistingField,
    isFormFieldInputRow,
    resolveCustomFieldViewForDroppedExistingField,
    submitNewCustomFieldToForm,
} from './sidebar/add-custom-field/helpers';
import type {
    EditCustomFieldSession,
    EditedFormFieldRef,
    NewCustomFieldFormPayload,
    SelectedExistingField,
} from './sidebar/add-custom-field/types';
import type {
    CustomFieldView,
    DraggableCardData,
} from './sidebar/blocks-and-design/content-blocks/types';
import type {SidebarState} from './sidebar/sidebar';
import type {Field, MultiPageForm} from './types';

interface Props extends RouteComponentProps {
    onPublishForm: (input: FormPublishInput) => Promise<any>;
    onClose: () => void;
    form: MultiPageFormFragment;
    formHeader?: React.ReactNode;
    refetchForm: () => Promise<any>;
    minimumOneEmailField?: boolean;
    isMeetingForm?: boolean;
    formDefaultValues: MultiPageForm;
    onUpdateForm: (input: FormUpdateInput) => Promise<any>;
    onDiscardForm: (formId: string) => Promise<any>;
    saveFailedErrorMessage: string | null | undefined;
    onClearError: () => void;
    onFieldCreateFailed: (message: string | null | undefined) => void;
}

export function FullPageFormEditorContent(props: Props) {
    const dispatch = useDispatch();
    const {refetchForm, onFieldCreateFailed} = props;
    const [sidebarState, setSidebarState] = useState<SidebarState>('blocks-and-design');
    const [customFieldView, setCustomFieldView] = useState<CustomFieldView | null>(null);
    const [editCustomFieldSession, setEditCustomFieldSession] =
        useState<EditCustomFieldSession | null>(null);
    const [sidebarDragCard, setSidebarDragCard] = useState<DraggableCardData | null>(null);
    const [reorderingFieldKey, setReorderingFieldKey] = useState<string | null>(null);
    const [fieldCreateAtIndex, setFieldCreateAtIndex] = useState<{
        pageIndex: number;
        insertIndex: number;
    } | null>(null);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [isConfirmingDiscard, setIsConfirmingDiscard] = useState<boolean>(false);
    const [shouldShowPublishChangesModal, setShouldShowPublishChangesModal] =
        useState<boolean>(false);

    const {handleSubmit, control, watch, getValues, setValue} = useForm({
        defaultValues: props.formDefaultValues,
        mode: 'all',
        reValidateMode: 'all',
        delayError: 400,
    });

    const dndPageFieldMutatorsRef = useRef<Map<number, PageFieldArrayOps>>(new Map());

    const [createField] = useMutation<CreateFieldMutation, CreateFieldMutationVariables>(
        CREATE_FIELD
    );

    const {createCustomField} = useCreateCustomField();

    const submitForm = useMemo(
        () =>
            _.debounce(
                // @ts-expect-error
                handleSubmit((formValues) => {
                    props.onUpdateForm(convertMultiPageFormToGraphQL(formValues));
                }),
                500
            ),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const formValues = JSON.stringify(watch());

    useDidMountEffect(() => {
        submitForm();
    }, [formValues, submitForm]);

    const clearDragUi = useCallback(() => {
        setSidebarDragCard(null);
        setReorderingFieldKey(null);
    }, []);

    const setSidebarStateAndClearFieldCreate = useCallback((state: SidebarState) => {
        setSidebarState(state);
        if (state !== 'add-custom-field' && state !== 'loading') {
            setFieldCreateAtIndex(null);
        }
    }, []);

    const openEditCustomFieldSession = useCallback(
        (session: EditCustomFieldSession) => {
            setCustomFieldView(session.view);
            setEditCustomFieldSession(session);
            setSidebarStateAndClearFieldCreate('edit-custom-field');
        },
        [setSidebarStateAndClearFieldCreate]
    );

    const onSubmitNewField = useCallback(
        (payload: NewCustomFieldFormPayload) => {
            if (!fieldCreateAtIndex) {
                return Promise.reject(new Error('No insert position for the new field.'));
            }
            const {pageIndex, insertIndex} = fieldCreateAtIndex;
            const ops = dndPageFieldMutatorsRef.current.get(pageIndex);
            if (!ops) {
                return Promise.reject(new Error('Form page is not ready.'));
            }

            return submitNewCustomFieldToForm({
                payload,
                formId: props.form.id,
                pageIndex,
                insertIndex,
                ops,
                createCustomField,
                createField,
                setValue,
                getValues,
                refetchForm,
                refreshFormSchemaInRedux: () => {
                    // @ts-expect-error redux-thunk
                    dispatch(fetchSchemaAction());
                },
            })
                .then((session) => {
                    setFieldCreateAtIndex(null);
                    if (session) {
                        openEditCustomFieldSession(session);
                    }
                })
                .catch((error) => {
                    onFieldCreateFailed(safelyGetGraphQLErrorMessage(error as GraphQLErrorObject));
                    setSidebarState('add-custom-field');
                });
        },
        [
            fieldCreateAtIndex,
            createCustomField,
            props.form.id,
            createField,
            getValues,
            setValue,
            refetchForm,
            dispatch,
            onFieldCreateFailed,
            openEditCustomFieldSession,
            setSidebarState,
        ]
    );

    const onPickExistingSchemaFieldForForm = useCallback(
        (field: FormSchemaProperty) => {
            if (!fieldCreateAtIndex) {
                return;
            }
            const {pageIndex, insertIndex} = fieldCreateAtIndex;
            const ops = dndPageFieldMutatorsRef.current.get(pageIndex);
            if (!ops) {
                return;
            }

            const input = buildFieldCreateInputForExistingCustomSchemaField(field, props.form.id);

            setSidebarState('loading');
            void createField({variables: {input}})
                .then((data) => {
                    const createdField = data?.data?.fieldCreate?.field;
                    if (!createdField) {
                        onFieldCreateFailed('Could not add field to form.');
                        setSidebarState('add-custom-field');
                        return;
                    }

                    const converted = convertGraphQLResponseToFormFieldState(createdField);
                    ops.insert(insertIndex, converted as Field);
                    refetchForm();

                    const view = resolveCustomFieldViewForDroppedExistingField(
                        field.displayType,
                        converted as Field
                    );

                    if (view && isFormFieldInputRow(converted as Field)) {
                        openEditCustomFieldSession({
                            view,
                            existingField: formFieldRowToSelectedExistingField(converted as Field),
                            editedFieldRef: {pageIndex, fieldIndex: insertIndex},
                        });
                    } else {
                        setSidebarStateAndClearFieldCreate('blocks-and-design');
                    }
                })
                .catch((error) => {
                    onFieldCreateFailed(safelyGetGraphQLErrorMessage(error as GraphQLErrorObject));
                    setSidebarState('add-custom-field');
                });
        },
        [
            fieldCreateAtIndex,
            createField,
            props.form.id,
            refetchForm,
            onFieldCreateFailed,
            openEditCustomFieldSession,
            setSidebarState,
            setSidebarStateAndClearFieldCreate,
        ]
    );

    const dragEndDeps = useMemo(
        () => ({
            clearDragUi,
            createField,
            dndPageFieldMutatorsRef,
            formId: props.form.id,
            onFieldCreateFailed: props.onFieldCreateFailed,
            openAddCustomFieldSidebar: (view: CustomFieldView) => {
                setCustomFieldView(view);
                setSidebarStateAndClearFieldCreate('add-custom-field');
            },
            openEditCustomFieldFromDrop: (payload: {
                view: CustomFieldView;
                existingField: SelectedExistingField;
                fieldRef: EditedFormFieldRef;
            }) => {
                setCustomFieldView(payload.view);
                setEditCustomFieldSession({
                    view: payload.view,
                    existingField: payload.existingField,
                    editedFieldRef: payload.fieldRef,
                });
                setSidebarStateAndClearFieldCreate('edit-custom-field');
            },
            refetchForm: props.refetchForm,
            setFieldCreateAtIndex,
            setSidebarStateForFieldCreate: setSidebarStateAndClearFieldCreate,
        }),
        [
            clearDragUi,
            createField,
            props.form.id,
            props.onFieldCreateFailed,
            props.refetchForm,
            setSidebarStateAndClearFieldCreate,
        ]
    );

    const handleDragStart = useCallback((event: DragStartEvent) => {
        handleFormEditorDragStart(event, setSidebarDragCard, setReorderingFieldKey);
    }, []);

    const handleDragEnd = useCallback(
        (event: DragEndEvent) => {
            handleFormEditorDragEnd(event, dragEndDeps);
        },
        [dragEndDeps]
    );

    return (
        <DndContext
            collisionDetection={formEditorListCollisionDetection}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragCancel={clearDragUi}
            onDragAbort={clearDragUi}
        >
            <FormEditorDndLayout>
                <FullPageEditor
                    title={props.form.name}
                    onBack={() => {
                        if (props.form.hasUnpublishedChanges) {
                            setIsConfirmingDiscard(true);
                        } else {
                            props.onClose();
                        }
                    }}
                    backButtonText={props.form.hasUnpublishedChanges ? 'Discard changes' : 'Back'}
                    previewPath={
                        props.isMeetingForm
                            ? undefined
                            : `/preview/forms/${props.form.publicFormId}`
                    }
                    previewButtonText={props.isMeetingForm ? undefined : 'Preview form'}
                    onSaveClick={() => {
                        if (props.form.isLiveForm) {
                            setShouldShowPublishChangesModal(true);
                        } else {
                            setIsSaving(true);
                            props.onPublishForm({form: {id: props.form.id}}).then(() => {
                                setIsSaving(false);
                                props.onClose();
                            });
                        }
                    }}
                    saveButtonText={props.form.isLiveForm ? 'Publish changes' : 'Save changes'}
                    isSaving={isSaving}
                    hasHeaderBorder={true}
                    content={
                        <FormEditorMainContent
                            formId={props.form.id}
                            formHeader={props.formHeader}
                            dndPageFieldMutatorsRef={dndPageFieldMutatorsRef}
                            control={control}
                            watch={watch}
                            getValues={getValues}
                            fieldCreateAtIndex={fieldCreateAtIndex}
                            saveFailedErrorMessage={props.saveFailedErrorMessage}
                            onClearError={props.onClearError}
                            minimumOneEmailField={props.minimumOneEmailField}
                            sidebarDragCard={sidebarDragCard}
                            reorderingFieldKey={reorderingFieldKey}
                            graphqlPages={props.form.editableVersion?.pages}
                            sidebarState={sidebarState}
                            setSidebarState={setSidebarStateAndClearFieldCreate}
                            customFieldView={customFieldView}
                            editCustomFieldSession={editCustomFieldSession}
                            setEditCustomFieldSession={setEditCustomFieldSession}
                            openEditCustomFieldSession={openEditCustomFieldSession}
                            setValue={setValue}
                            onPickExistingSchemaFieldForForm={onPickExistingSchemaFieldForForm}
                            onSubmitNewField={onSubmitNewField}
                        />
                    }
                />
                {isConfirmingDiscard && (
                    <ConfirmationDialog
                        title='Discard changes'
                        message='If you leave now, your changes will be discarded. Would you like to go back?'
                        confirmText='Yes, discard changes'
                        cancelText='No, stay here'
                        onCancel={() => {
                            setIsConfirmingDiscard(false);
                        }}
                        onConfirm={() => {
                            props.onDiscardForm(props.form.id).then(() => {
                                props.onClose();
                            });
                        }}
                        isConfirmDestructive={true}
                    />
                )}
                {shouldShowPublishChangesModal && (
                    <PublishModal
                        formId={props.form.id}
                        isMeetingForm={props.isMeetingForm}
                        onClose={() => {
                            setShouldShowPublishChangesModal(false);
                        }}
                        onPublish={() => {
                            props.onPublishForm({form: {id: props.form.id}}).then(() => {
                                setShouldShowPublishChangesModal(false);
                                props.onClose();
                            });
                        }}
                    />
                )}
            </FormEditorDndLayout>
        </DndContext>
    );
}
